import { CanReceivePeppol } from "company/detail/components/CanReceivePeppol";
import { FinancialRiskEdit } from "company/detail/components/FinancialRiskEdit";
import { getInstruction, BankFormState, CompanyFormState, CompanyFieldMetaData } from "shared/utils/fieldsHelper/companyFields";
import FinancialRiskCard from "components/FinancialRisk/FinancialRiskCard";
import { FormSection } from "components/MainSectionTemplate/types";
import { GlobalLocationNumberInput } from "components/ValidatedFields/GlobalLocationNumberInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import VatNumberDetails from "components/VatNumber/VatNumberDetails";
import React from "react";

export const financialDetailSection = (
  companyFormState: CompanyFormState,
  bankFormState: BankFormState,
  fieldMetaData: CompanyFieldMetaData
): FormSection => {
  return {
    type: "collapsable",
    title: RESX.Financial.FinancialDetails,
    key: "companyFinancialDetails",
    fields: [{
      ...fieldMetaData.financialRisk.fields(),
      key: "financialRisk",
      label: RESX.Promoter.FinancialRisk,
      render: () => <FinancialRiskCard financialRisk={companyFormState.financialRisk} />,
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.financialRisk,
        render: ({ value, onChange }) => (
          <FinancialRiskEdit
            value={value}
            onChange={onChange}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: server.dto.FinancialRisk) => {
          const instruction = getInstruction(companyFormState, bankFormState, {
            financialRisk: {
              comment: value.level === "unknown" ? null : value.comment,
              level: value.level
            }
          });

          return window.CoreApi.Company.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.euVatNumber.fields(),
      key: "euVatNumber",
      render: () => <VatNumberDetails companyId={companyFormState.companyId} euVatNumber={companyFormState.euVatNumber} />,
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.euVatNumber,
        useCustomSavingState: true,
        render: ({ value, onChange, submit, cancel, isSaving }) => (
          <OptionalTextInput
            focusOnMount
            value={value.number}
            onChange={v => onChange({ ...value, number: v })}
            maxLength={serverReference.constants.company.maxEuVatNumberLength}
            onEnter={submit}
            onEsc={cancel}
            loading={isSaving}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: server.dto.EuVatNumber) => {
          const instruction = getInstruction(companyFormState, bankFormState, { euVatNumber: value.number });
          return window.CoreApi.Company.Update(instruction);
        }
      },
    },
    {
      ...fieldMetaData.fiscalNumber1.fields(),
      key: "fiscalNumber1",
      render: () => <div>{companyFormState.fiscalNumber1}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.fiscalNumber1,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxFiscalNumber1Length}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(companyFormState, bankFormState, { fiscalNumber1: value });
          return window.CoreApi.Company.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.fiscalNumber2.fields(),
      key: "fiscalNumber2",
      render: () => <div>{companyFormState.fiscalNumber2}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.fiscalNumber2,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxFiscalNumber2Length}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(companyFormState, bankFormState, { fiscalNumber2: value });
          return window.CoreApi.Company.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.legalStructure.fields(),
      key: "legalStructure",
      render: () => <div>{companyFormState.legalStructure}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.legalStructure,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxLegalStructureLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(companyFormState, bankFormState, { legalStructure: value });
          return window.CoreApi.Company.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.businessRegistrationNumber.fields(),
      key: "businessRegistrationNumber",
      render: () => <div>{companyFormState.businessRegistrationNumber}</div>,
      inPageEdit: {
        fullWidth: true,
        initialValue: companyFormState.businessRegistrationNumber,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.company.maxBusinessRegistrationNumberLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getInstruction(companyFormState, bankFormState, { businessRegistrationNumber: value });
          return window.CoreApi.Company.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.globalLocationNumber.fields(),
      key: "globalLocationNumber",
      label: "Global Location Number (GLN)",
      render: () => <div>{companyFormState.globalLocationNumber}</div>,
      inPageEdit: {
        initialValue: companyFormState.globalLocationNumber,
        fullWidth: true,
        render: ({ value, onChange, submit, cancel }) => (
          <GlobalLocationNumberInput
            focusOnMount
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value) => {
          const instruction = getInstruction(companyFormState, bankFormState, {
            globalLocationNumber: value
          });
          return window.CoreApi.Company.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.internalId.fields(),
      key: "internalId",
      label: RESX.Company.CustomerNumber,
      render: () => <div>{companyFormState.internalId}</div>,
      inPageEdit: {

        initialValue: companyFormState.internalId,
        fullWidth: true,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            maxLength={serverReference.constants.company.maxInternalIdLength}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value) => {
          const instruction = getInstruction(companyFormState, bankFormState, {
            internalId: value
          });
          return window.CoreApi.Company.Update(instruction);
        }
      }
    }, {
      ...fieldMetaData.expectPurchaseOrderNumber.fields(),
      key: "expectPurchaseOrderNumber",
      label: RESX.Company.ExpectsPONumber,
      render: () => <YesNoText value={companyFormState.expectPurchaseOrderNumber} />,
      inPageEdit: {
        initialValue: companyFormState.expectPurchaseOrderNumber,
        render: ({ value, onChange }) => (
          <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />
        ),
        fullWidth: true,
        type: "custom",
        submitChange: (value: boolean) => {
          const instruction = getInstruction(companyFormState, bankFormState, {
            expectPurchaseOrderNumber: value
          });
          return window.CoreApi.Company.Update(instruction);
        }
      }
    }, {
      key: "peppolCompliance",
      label: "Peppol",
      render: () => <CanReceivePeppol companyId={companyFormState.companyId} />,
      ...fieldMetaData.peppol.fields(),
    }]
  };
}